import React, { useState, useContext, useEffect, useRef } from 'react';
import Styled from 'styled';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useQuery } from '@apollo/client/react/hooks';
import { intlShape, FormattedMessage, IntlShape, defineMessages } from 'react-intl';
import { mapAfter } from 'apollo-relay/update-query';

import ScrollView from 'lib/scroll/scroll-view';
import SVGIcon from 'lib/images/svg-icon/svg-icon';

import { SiteContext } from 'four-nets';
import circleArrow from 'four-nets/icons/svg/icon_arrow-collapse.svg';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';
import { ViewerContext } from 'four-nets/user/viewer/viewer';
import { UserBlogQuery } from 'four-nets/photoblog/user-blog/user-blog.graphql';
import CreateUserBrand from 'four-nets/user-brand/create-user-brand/create-user-brand';
import { hideExpiredVendorContent } from 'four-nets/vendor/permissions';

import initWidget from './vendor-user-brands-widget.js';
import VendorUserBrand from './vendor-user-brand';
import query, { VendorUserBrandsQuery, VendorUserBrandsQueryVariables } from './vendor-user-brands.graphql';
import defaultStyles from './vendor-user-brands.scss';

export type VendorFragment = NonNullable<
    NonNullable<
        NonNullable<NonNullable<NonNullable<NonNullable<UserBlogQuery>['users']>['edges']>[0]>['node']
    >['profile']
>['vendor'];

export interface Props {
    styles: typeof defaultStyles;
    scrollToUserBrands?: boolean;
    username: string;
    vendor?: VendorFragment;
}

interface ShareVendorUserBrandsProps {
    styles: typeof defaultStyles;
    intl: IntlShape;
    vendorName: string;
    vendorUsername: string;
}

const ShareVendorUserBrands: React.FC<ShareVendorUserBrandsProps> = props => {
    const { intl, vendorName, vendorUsername, styles } = props;

    const site = useContext(SiteContext);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const [width, setWidth] = useState(220);
    const [height, setHeight] = useState(400);
    const changeWdith = (event: React.ChangeEvent<HTMLInputElement>) => {
        setWidth(parseInt(event.target.value, 10));
    };
    const changeHeight = (event: React.ChangeEvent<HTMLInputElement>) => {
        setHeight(parseInt(event.target.value, 10));
    };

    const getCode = () => {
        if (!site) {
            return '';
        }

        const linkTextMsg = defineMessages({
            'VENDOR_WIDGET_SEO_TEXT_%(server_name)s_FOR_%(vendor_name)s': {
                id: 'VENDOR_WIDGET_SEO_TEXT_%(server_name)s_FOR_%(vendor_name)s',
            },
        });

        return `<div id="recom-widget-root"><a href="${
            site.url
        }/catalog/vendor/${vendorUsername}/">${intl.formatMessage(
            linkTextMsg['VENDOR_WIDGET_SEO_TEXT_%(server_name)s_FOR_%(vendor_name)s'],
            {
                server_name: site.name,
                vendor_name: vendorName,
            }
        )}</a></div><script id="recom-widget-script" src="${
            site.url
        }/vendor/recomm-widget/${vendorUsername}?width=${width}&height=${height}"></script>`;
    };

    const [code, setCode] = useState(getCode());

    useEffect(() => {
        setCode(getCode());
        initWidget(site ? site.countryCode : 'sk', vendorUsername, width, height);
    }, [width, height]);

    const copyCode = () => {
        if (document && textareaRef && textareaRef.current) {
            textareaRef.current.select();
            document.execCommand('copy');
        }
    };

    return (
        <div>
            <h2 className={styles.shareHeading}>
                <FormattedMessage id='VENDOR_TAB_SHARE_RECOMMENDATIONS' />
            </h2>
            <p className={styles.shareText}>
                <FormattedMessage id='VENDOR_WIDGET_MAIN_TEXT_%(webpage)s' values={{ webpage: '' }} />
            </p>
            <div>
                <h3 className={styles.shareSectionHeading}>
                    <FormattedMessage id='VENDOR_WIDGET_VIEW_SETTINGS' />
                </h3>
                <div className={styles.shareEditor}>
                    <div className={styles.shareInputFields}>
                        <label className={styles.shareInputField}>
                            <FormattedMessage id='WIDTH' />
                            <input value={width} onChange={changeWdith} className={styles.shareInput} type='number' />
                        </label>
                        <label className={styles.shareInputField}>
                            <FormattedMessage id='HEIGHT' />
                            <input value={height} onChange={changeHeight} className={styles.shareInput} type='number' />
                        </label>
                    </div>
                    <div className={styles.shareTextareaWrapper}>
                        <div>
                            <textarea ref={textareaRef} className={styles.shareTextarea} readOnly={true} value={code} />
                        </div>
                        <div className={styles.shareCopyButtonWrapper}>
                            <button className={classnames(styles.button, styles.shareCopyButton)} onClick={copyCode}>
                                <FormattedMessage id='COPY' />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div id='recom-widget-root' />
        </div>
    );
};

const VendorUserBrands: React.FC<Props> = (props, context) => {
    const { styles, username, scrollToUserBrands, vendor } = props;
    const { scrollView, authCheck, intl } = context;

    const { viewer } = useContext(ViewerContext);

    const [open, setOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [shareOpened, setShareOpened] = useState(false);

    const { data, refetch, loading, fetchMore } = useQuery<VendorUserBrandsQuery, VendorUserBrandsQueryVariables>(
        query,
        {
            variables: { username },
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'cache-first',
        }
    );

    const isReviewedVendor = Boolean(viewer && viewer.username === username);
    const viewerIsVendor = Boolean(viewer && viewer.profile && viewer.profile.vendor);

    const totalCount = (data && data.userBrands && data.userBrands.totalCount) || 0;
    const endCursor = data && data.userBrands && data.userBrands.pageInfo && data.userBrands.pageInfo.endCursor;
    const hasNextPage = data && data.userBrands && data.userBrands.pageInfo && data.userBrands.pageInfo.hasNextPage;
    const fetchMoreFn = hasNextPage
        ? () => {
              return fetchMore({
                  variables: { after: endCursor },
                  updateQuery: (
                      previousResult = {} as VendorUserBrandsQuery,
                      { fetchMoreResult = {} as VendorUserBrandsQuery }: { fetchMoreResult?: VendorUserBrandsQuery }
                  ) => {
                      return { userBrands: mapAfter(previousResult.userBrands, fetchMoreResult.userBrands) };
                  },
              });
          }
        : undefined;

    return (hideExpiredVendorContent(vendor as any) && !isReviewedVendor) ||
        !(data && data.userBrands && data.userBrands.edges && data.userBrands.edges) ? null : (
        <div
            className={classnames(styles.wrapper, { [styles.collapsed]: collapsed })}
            ref={ref => {
                if (scrollView && scrollToUserBrands) {
                    scrollView.scrollToRef({
                        ref,
                        offset: -100,
                    });
                }
            }}
        >
            <LoadingWrapperView loading={loading}>
                <button
                    onClick={() => {
                        setCollapsed(!collapsed);
                    }}
                    className={classnames(styles.heading, { [styles.collapsed]: collapsed })}
                >
                    {totalCount || isReviewedVendor ? (
                        <FormattedMessage id='%(counter)s_RECOMMENDATIONS' values={{ counter: totalCount }} />
                    ) : (
                        <FormattedMessage id='RECOMMEND' />
                    )}
                    {totalCount > 0 ? (
                        <div
                            className={classnames(styles.iconWrapper, {
                                [styles.collapsed]: collapsed,
                            })}
                        >
                            <SVGIcon style={{ image: styles.icon }} src={circleArrow} />
                        </div>
                    ) : null}
                </button>
                <div className={styles.userBrands}>
                    {data.userBrands.edges.map(edge =>
                        edge && edge.node ? (
                            <VendorUserBrand
                                key={edge.node.id}
                                isReviewedVendor={isReviewedVendor}
                                username={username}
                                refetch={refetch}
                                {...edge.node}
                            />
                        ) : null
                    )}
                    {shareOpened && vendor ? (
                        <div className={styles.shareWrapper}>
                            <ShareVendorUserBrands
                                intl={intl}
                                vendorName={vendor.name}
                                vendorUsername={vendor.user.username}
                                styles={styles}
                            />
                        </div>
                    ) : null}
                    {!open ? (
                        <div className={styles.buttons}>
                            {hasNextPage && !shareOpened ? (
                                <button
                                    disabled={loading}
                                    className={styles.button}
                                    onClick={() => {
                                        if (fetchMoreFn) {
                                            fetchMoreFn();
                                        }
                                    }}
                                >
                                    <FormattedMessage id='BUTTON_SHOW_MORE' />
                                </button>
                            ) : null}
                            {isReviewedVendor ? (
                                <button
                                    className={classnames(styles.button, { [styles.add]: !shareOpened })}
                                    onClick={() => setShareOpened(!shareOpened)}
                                >
                                    <FormattedMessage
                                        id={shareOpened ? 'BUTTON_LABEL_CLOSE' : 'VENDOR_TAB_SHARE_RECOMMENDATIONS'}
                                    />
                                </button>
                            ) : null}
                            {!viewerIsVendor ? (
                                <button
                                    onClick={() => {
                                        if (authCheck) {
                                            authCheck(() => setOpen(true));
                                        } else {
                                            setOpen(true);
                                        }
                                    }}
                                    className={classnames(styles.button, styles.add)}
                                >
                                    <FormattedMessage id='ADD_RECOMMENDATION_SUBMIT' values={{ gender: 'v' }} />
                                </button>
                            ) : null}
                        </div>
                    ) : !viewerIsVendor ? (
                        <CreateUserBrand refetch={refetch} vendor={vendor} open={open} close={() => setOpen(false)} />
                    ) : null}
                </div>
            </LoadingWrapperView>
        </div>
    );
};

VendorUserBrands.contextTypes = {
    scrollView: PropTypes.object,
    authCheck: PropTypes.func,
    intl: intlShape,
};

export default Styled(defaultStyles)(VendorUserBrands);
